import React, { useEffect, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { App, Modal, Form, Select, FormInstance, Button } from 'antd'
import { useIntl } from 'react-intl'
import { useManualQuery } from 'graphql-hooks'
import { getSlackBotDetails } from '../../graphql/custom-queries'
import * as logger from '../../services/logger'

import Api from '@vacationtracker/shared/services/api'
import { SLACK_REQUIRED_BOT_SCOPES, SLACK_REQUIRED_USER_SCOPES } from '@vacationtracker/shared/data/slack'
import { SlackAuth } from '../../services/auth/slack/slack'
import { useAppSelector } from '../../store/hooks'
import { selectAuthUserSlice } from '../../store/auth-user-slice'

import IntlMessages from '../../util/IntlMessages'
import { filterOptions } from '../../util/filter-options'

import { ISlackBotDetails } from '../../types/slack'
import { selectAuthCompanySlice } from '../../store/auth-company-slice'
import Paragraph from 'antd/lib/typography/Paragraph'

interface IChannel {
  id: string
  name?: string
}

interface ISelectChannelFromSlack {
  name: string[]
  form: FormInstance
  onSelectDestination: (e) => void
  setChannelName: (e) => void
  newDesign?: boolean
  filterByWidth?: number
}

const { Option } = Select

if (!process.env.REACT_APP_SLACK_CLIENT_ID) {
  throw new Error('Client ID are required')
}
const slackAuth = new SlackAuth(process.env.REACT_APP_SLACK_CLIENT_ID)

export const SelectChannelFromSlack = ({ name, onSelectDestination, setChannelName, form, newDesign, filterByWidth }: ISelectChannelFromSlack): React.ReactElement => {
  const { formatMessage } = useIntl()
  const history = useHistory()
  const { notification } = App.useApp()

  const { authUser } = useAppSelector(selectAuthUserSlice)
  const { authCompany } = useAppSelector(selectAuthCompanySlice)
  const [channels, setChannels] = useState<IChannel[]>([])
  const [showSlackLoginRequiredModal, setShowSlackLoginRequiredModal] = useState<boolean>(false)
  const [slackBotId, setSlackBotId] = useState<string>()
  const [getSlackBotDetailsQuery] = useManualQuery<ISlackBotDetails, { id: string }>(getSlackBotDetails)

  useEffect(() => {
    fetchSlackData()
  }, [])

  const fetchSlackData = async (id = authUser.id) => {
    try {
      const response = await getSlackBotDetailsQuery({ variables: { id } })
      if (!response.data || response.error) throw response.error
      if (!response.data.slackId) {
        throw new Error('Slack ID not found')
      }
      setSlackBotId(response.data.slackId.slackBotId)
      await fetchSlackChannels()
    } catch (error) {
      if (id === authUser.id) {
        // TODO: @slobodan remove this after migration
        // Fetch user without prefix
        return fetchSlackData(authUser.id.replace('slack-', ''))
      }
      logger.error('error', error)
    }
  }

  const fetchSlackChannels = async () => {
    try {
      slackAuth.setTokensFromStorage()
      await slackAuth.authTest()
      const response = await slackAuth.conversationsList()

      const channelsData = response.map((channel) => {
        return { id: channel.id, name: `${channel.name}${channel.is_private ? ` ${formatMessage({ id: 'components.selectChannelFormSlack.privateChannel' })}` : ''}` }
      }) as IChannel[]
      setChannels(channelsData)

      const formData = form.getFieldsValue()
      if (name[0] && formData[name[0]]) {
        const channelId = formData[name[0]].addresses
        if (channelId) {
          const channel = channelsData.find((v) => channelId === v.id)
          setChannelName(channel?.name)
        }
      }
    } catch (error) {
      logger.error('error', error)
      if (error?.error === 'not_authed' || error?.error === 'invalid_auth' || error?.error === 'missing_scope' || error?.error === 'token_revoked') {
        setShowSlackLoginRequiredModal(true)
      }
    }
  }

  const getSlackTokens = () => {
    slackAuth
      .signin(SLACK_REQUIRED_BOT_SCOPES, SLACK_REQUIRED_USER_SCOPES)
      .then(async (response) => {
        if ((!response.enterprise && response.team.id !== authCompany?.organizationId) || (response.enterprise && response.enterprise?.id !== authCompany?.organizationId)) {
          wrongSlackAccount()
          return
        }
        slackAuth.setUserToken(response.authed_user.access_token)
        try {
          await Api.post('/slack/update-tokens', {
            token: response.authed_user.access_token,
            botToken: response.access_token,
          })
        } catch (error) {
          logger.warning('UPDATE TOKEN ERROR', error.response)
        }
        setShowSlackLoginRequiredModal(false)
        await fetchSlackChannels()
      })
      .catch((error) => {
        logger.warning('SLACK SIGNIN ERROR', error)
        handleErrorNotification(
          { message: formatMessage({ id: 'components.selectChannelFormSlack.toProccedPleaseLogin' }) },
          undefined,
          formatMessage({ id: 'components.selectChannelFormSlack.missingSlackScopes' })
        )
      })
  }
  const wrongSlackAccount = () => {
    slackAuth.removeTokens()
    notification.error({
      message: formatMessage({ id: 'error.slack.wrongAccount' }),
      key: 'wrong-slack-account',
      description: (
        <div>
          <Paragraph>{formatMessage({ id: 'errors.slack.wrongSlackAccountDescription' })}</Paragraph>
          <Button
            onClick={() => {
              getSlackTokens()
              notification.destroy('wrong-slack-account')
            }}
          >
            {formatMessage({ id: 'error.slack.logInWithDifferentAccount' })}
          </Button>
        </div>
      ),
      duration: 0,
    })
    setShowSlackLoginRequiredModal(false)
  }

  const handleErrorNotification = (error, correlationId?: string, title?: string): void => {
    const description = correlationId
      ? formatMessage({ id: 'error.notificationGeneral' }, { correlationId })
      : error.response?.data?.message
        ? error.response?.data?.message
        : error.message
          ? error.message
          : JSON.stringify(error)
    notification.error({
      message: title ? title : formatMessage({ id: 'error.notificationGeneral' }),
      description,
      duration: 0,
    })
  }

  const onSelect = async (channelId) => {
    onSelectDestination(channelId)
    const channelName = channels.find((channel) => channel.id === channelId)?.name
    setChannelName(channelName)

    try {
      if (!slackBotId) {
        handleErrorNotification({ message: formatMessage({ id: 'components.selectChannelFormSlack.slackBotIdIsRequred' }) })
        return
      }
      slackAuth.setTokensFromStorage()
      await slackAuth.authTest()
      await slackAuth.conversationsInvite(channelId, slackBotId)
    } catch (error) {
      if (error.error === 'not_in_channel') {
        handleErrorNotification({ message: formatMessage({ id: 'components.selectChannelFormSlack.youCanAddTheBotOnlyOnTheChannels' }, { channelName }) })
        return
      }
      if (error.error === 'invitee_is_not_a_member_of_this_workspace') {
        setShowSlackLoginRequiredModal(true)
        return
      }
      if (error.error !== 'already_in_channel') {
        handleErrorNotification({ message: `${formatMessage({ id: 'components.selectChannelFormSlack.slackError' })}: ${error.error}${error.needed ? ` ${error.needed}` : ''}` })
        return
      }
    }
  }

  return (
    <>
      <Form.Item
        name={name}
        rules={[{ required: true, message: <IntlMessages id="form.inputRequired" /> }]}
        style={{ width: newDesign ? `calc(100% - ${filterByWidth}px)` : 250, marginRight: 0 }}
      >
        <Select
          showSearch
          filterOption={filterOptions}
          disabled={!channels.length}
          loading={!channels.length}
          onSelect={onSelect}
          className={newDesign ? '' : 'select-channel-from-slack'}
        >
          {channels.map((channel) => (
            <Option key={channel.id} value={channel.id}>
              {channel.name}
            </Option>
          ))}
        </Select>
      </Form.Item>

      {showSlackLoginRequiredModal && (
        <Modal
          title={formatMessage({ id: 'error.slack.usersLoginRequiredTitle' })}
          open={showSlackLoginRequiredModal}
          onOk={getSlackTokens}
          onCancel={() => {
            setShowSlackLoginRequiredModal(false)
            history.goBack()
          }}
        >
          <p>
            <IntlMessages id="slack.notificationsLoginRequiredDescription1" />
          </p>
          <p>
            <IntlMessages id="slack.notificationsLoginRequiredDescription2" />
          </p>
        </Modal>
      )}
    </>
  )
}
